/**
 * Suppliers — the list the supplier page's breadcrumb returns to.
 *
 * One row per supplier (sup_supplier, keyed on its CIS supplier number), with
 * where its account stands. Accounts that need something come first, so the
 * list doubles as "which suppliers need me today". The composed view
 * (`/views/suppliers`) searches, orders and pages it; the page holds only the
 * search box and the page number.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { SupplierListPage } from "@shared-core/data/purchase-ledger-types";
import { formatMoney } from "@shared-core/domain/purchase-ledger";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { paths } from "@shared-core/routing/routes";
import { NewSupplierDialog } from "../app/components/domain/purchasing/NewSupplierDialog";
import {
  SupplierHealthPill,
  SupplierStatusPill,
} from "../app/components/domain/purchasing/SupplierPills";
import { ListPagination } from "../app/components/patterns/ListPagination";
import { Alert, AlertDescription } from "../app/components/primitives/alert";
import { Input } from "../app/components/primitives/input";
import { Skeleton } from "../app/components/primitives/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";

const PAGE_SIZE = 50;

type LoadState =
  | { kind: "loading" }
  | { kind: "failed"; message: string }
  | { kind: "loaded"; list: SupplierListPage };

export function SuppliersPage() {
  const adapter = useDataAdapter();
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [reloads, setReloads] = useState(0);
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const debouncedQuery = useDebouncedValue(query, 300);

  useEffect(() => {
    let cancelled = false;
    adapter
      .listSuppliers({
        q: debouncedQuery.trim() || undefined,
        sort: "attention,name",
        page,
        pageSize: PAGE_SIZE,
      })
      .then((list) => {
        if (!cancelled) setState({ kind: "loaded", list });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({
            kind: "failed",
            message:
              err instanceof Error
                ? err.message
                : "The suppliers did not load.",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, debouncedQuery, page, reloads]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Suppliers</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Who we buy from, and where each account stands. Those needing
            something come first.
          </p>
        </div>
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <Input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
            placeholder="Supplier name or number…"
            aria-label="Search suppliers"
            className="h-9 w-full sm:w-72"
          />
          {state.kind === "loaded" && (
            <NewSupplierDialog
              list={state.list}
              onCreated={() => setReloads((n) => n + 1)}
            />
          )}
        </div>
      </div>

      {state.kind === "loading" && (
        <div className="space-y-2" aria-label="Loading suppliers">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      )}
      {state.kind === "failed" && (
        <Alert variant="destructive">
          <AlertDescription>
            Could not load the suppliers: {state.message}
          </AlertDescription>
        </Alert>
      )}
      {state.kind === "loaded" && (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Supplier</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Needs</TableHead>
                <TableHead className="text-right">To confirm</TableHead>
                <TableHead className="text-right">Open POs</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {state.list.items.map((supplier) => (
                <TableRow key={supplier.id}>
                  <TableCell>
                    <Link
                      to={paths.supplier({ supplierNo: supplier.supplierNo })}
                      className="font-medium hover:underline"
                    >
                      {supplier.name}
                    </Link>
                    <div className="text-muted-foreground text-xs">
                      {supplier.supplierNo}
                      {supplier.supplierCode &&
                        ` · Tharstern ${supplier.supplierCode}`}
                    </div>
                  </TableCell>
                  <TableCell>
                    <SupplierStatusPill status={supplier.status} />
                  </TableCell>
                  <TableCell>
                    <SupplierHealthPill health={supplier.health} />
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {supplier.position.toConfirmCount > 0
                      ? formatMoney(supplier.position.toConfirmNet)
                      : "—"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {supplier.position.openOrderCount > 0
                      ? formatMoney(supplier.position.openOrdersNet)
                      : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {state.list.items.length === 0 && (
            <p className="text-muted-foreground text-sm">
              {debouncedQuery.trim()
                ? "No supplier matches that."
                : "No suppliers yet."}
            </p>
          )}
          <ListPagination
            page={state.list.page}
            totalPages={state.list.totalPages}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}
